import { Button } from '@/components/ui/button'
import {
  getAvailableOptionalAddons,
  getChildSaleLines,
} from '@/components/pos/posCart'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import('@/types/api').Sale} props.sale
 * @param {import('@/types/api').SaleLine} props.line
 * @param {import('@/types/api').ServiceAddonOption[]} [props.addonOptions]
 * @param {boolean} props.disabled
 * @param {(parentLineId: number, addonServiceId: number) => void} props.onAttach
 * @param {(addonLineId: number) => void} props.onDetach
 */
export function PosLineAddonModal({
  open,
  onClose,
  sale,
  line,
  addonOptions = [],
  disabled,
  onAttach,
  onDetach,
}) {
  if (!open || !line) return null

  const childLines = getChildSaleLines(sale, line.id)
  const availableAddons = getAvailableOptionalAddons(addonOptions, childLines)

  return (
    <div
      className="pos-v2-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Service add-ons"
      onClick={disabled ? undefined : onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border bg-white p-4 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-sm font-bold text-slate-900">{line.description}</p>
        <p className="mb-3 text-xs text-slate-500">Attach or remove add-ons</p>

        {childLines.length > 0 ? (
          <ul className="mb-3 space-y-1">
            {childLines.map((addonLine) => {
              const isRequired = addonOptions.some(
                (option) => option.addon_service_id === addonLine.service_id && option.required,
              )

              return (
                <li
                  key={addonLine.id}
                  className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
                >
                  <span>{addonLine.description}</span>
                  {!isRequired ? (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onDetach(addonLine.id)}
                      className="text-xs font-bold text-red-600"
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="text-[10px] text-amber-800">Required</span>
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="mb-3 text-xs text-slate-500">No add-ons attached.</p>
        )}

        {availableAddons.length > 0 ? (
          <div className="mb-4 flex flex-wrap gap-1">
            {availableAddons.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => onAttach(line.id, option.addon_service_id)}
                className="rounded-md border border-primary/30 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/5"
              >
                + {option.addon?.name ?? 'Add-on'}
              </button>
            ))}
          </div>
        ) : null}

        <Button type="button" className="w-full" disabled={disabled} onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  )
}
